import {
  useEffect,
  useCallback,
  useState,
  useRef,
  type MouseEvent,
  type TouchEvent,
} from "react";
import { useCalibration } from "../../contexts/CalibrationContext";
import { MODES } from "../../utils/constants";

export default function CalibrationCanvas() {
  const {
    currentMode,
    calibPoints,
    isSurveyLoaded,
    calibrationSvgRef,
    surveyContainerRef,
    setCalibPoint,
    setAllCalibPoints,
    addCornerPoint,
    nextCornerIndex,
    allCornersMarked,
    getNextCornerName,
  } = useCalibration();

  const [draggingHandle, setDraggingHandle] = useState<string | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const initialPointRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (currentMode === MODES.CALIBRATE && surveyContainerRef.current) {
      const container = surveyContainerRef.current;
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      const padding = Math.min(50, w * 0.1, h * 0.1);
      setAllCalibPoints({
        tl: { x: padding, y: padding },
        tr: { x: w - padding, y: padding },
        br: { x: w - padding, y: h - padding },
        bl: { x: padding, y: h - padding },
      });
    }
  }, [currentMode, surveyContainerRef, setAllCalibPoints]);

  const getEventCoordinates = useCallback(
    (
      e:
        | MouseEvent
        | TouchEvent
        | globalThis.MouseEvent
        | globalThis.TouchEvent,
    ) => {
      if ("touches" in e && e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return {
        x: (e as globalThis.MouseEvent).clientX,
        y: (e as globalThis.MouseEvent).clientY,
      };
    },
    [],
  );

  const handleSvgClick = useCallback(
    (e: MouseEvent<SVGSVGElement>) => {
      if (currentMode !== MODES.MARK_CORNERS || allCornersMarked) return;

      const container = surveyContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      addCornerPoint({ x, y });
    },
    [currentMode, allCornersMarked, surveyContainerRef, addCornerPoint],
  );

  const handlePointerDown = useCallback(
    (
      e: MouseEvent<SVGCircleElement> | TouchEvent<SVGCircleElement>,
      position: string,
    ) => {
      if (currentMode !== MODES.CALIBRATE) return;

      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();

      const coords = getEventCoordinates(
        e as unknown as MouseEvent | TouchEvent,
      );
      setDraggingHandle(position);
      dragStartRef.current = coords;
      initialPointRef.current = {
        ...(calibPoints as unknown as Record<string, { x: number; y: number }>)[
          position
        ],
      };
    },
    [currentMode, calibPoints, getEventCoordinates],
  );

  const handlePointerMove = useCallback(
    (e: globalThis.MouseEvent | globalThis.TouchEvent) => {
      if (!draggingHandle || !dragStartRef.current || !initialPointRef.current)
        return;

      // Prevent page scrolling on mobile
      if (e.cancelable) {
        e.preventDefault();
      }

      const coords = getEventCoordinates(e);
      const dx = coords.x - dragStartRef.current.x;
      const dy = coords.y - dragStartRef.current.y;

      setCalibPoint(draggingHandle, {
        x: initialPointRef.current.x + dx,
        y: initialPointRef.current.y + dy,
      });
    },
    [draggingHandle, getEventCoordinates, setCalibPoint],
  );

  const handlePointerUp = useCallback(() => {
    setDraggingHandle(null);
    dragStartRef.current = null;
    initialPointRef.current = null;
  }, []);

  useEffect(() => {
    if (!draggingHandle) return;

    const onMove = (e: globalThis.MouseEvent | globalThis.TouchEvent) =>
      handlePointerMove(e);
    const onUp = () => handlePointerUp();

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    document.addEventListener("touchcancel", onUp);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      document.removeEventListener("touchcancel", onUp);
    };
  }, [draggingHandle, handlePointerMove, handlePointerUp]);

  if (
    !isSurveyLoaded ||
    (currentMode !== MODES.CALIBRATE && currentMode !== MODES.MARK_CORNERS)
  ) {
    return null;
  }

  const { tl, tr, br, bl } = calibPoints;

  if (currentMode === MODES.CALIBRATE && (!tl || !tr || !br || !bl)) {
    return null;
  }

  const hasPolygon = tl && tr && br && bl;
  const polygonPath = hasPolygon
    ? `M${tl.x},${tl.y} L${tr.x},${tr.y} L${br.x},${br.y} L${bl.x},${bl.y} Z`
    : null;

  const markedPoints = Object.entries(calibPoints).filter(
    ([, point]) => point !== null,
  ) as [string, { x: number; y: number }][];

  return (
    <svg
      ref={calibrationSvgRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 20,
        pointerEvents: "auto",
        touchAction: "none", // Prevent browser scrolling on touch
        cursor:
          currentMode === MODES.MARK_CORNERS && !allCornersMarked
            ? "crosshair"
            : "default",
      }}
      onClick={handleSvgClick}
    >
      {currentMode === MODES.MARK_CORNERS && !allCornersMarked && (
        <text
          x="50%"
          y="30"
          textAnchor="middle"
          fill="#2f5241"
          fontSize="14"
          fontWeight="bold"
          style={{ pointerEvents: "none" }}
        >
          Click to mark: {getNextCornerName()} corner ({nextCornerIndex + 1}/4)
        </text>
      )}

      {currentMode === MODES.MARK_CORNERS && allCornersMarked && (
        <text
          x="50%"
          y="30"
          textAnchor="middle"
          fill="#2f5241"
          fontSize="14"
          fontWeight="bold"
          style={{ pointerEvents: "none" }}
        >
          All corners marked! Click "Finish calibration" to complete.
        </text>
      )}

      {polygonPath && (
        <path
          d={polygonPath}
          fill="none"
          stroke="orange"
          strokeWidth="2"
          strokeDasharray="4"
          style={{ pointerEvents: "none" }}
        />
      )}

      {currentMode === MODES.MARK_CORNERS && markedPoints.length >= 2 && (
        <polyline
          points={markedPoints.map(([, p]) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#DDA248"
          strokeWidth="2"
          strokeDasharray="5,5"
          style={{ pointerEvents: "none" }}
        />
      )}

      {currentMode === MODES.CALIBRATE &&
        Object.entries(calibPoints).map(
          ([pos, point]) =>
            point && (
              <g key={pos}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="25"
                  fill="transparent"
                  style={{ pointerEvents: "auto", cursor: "move" }}
                  onMouseDown={(e) => handlePointerDown(e, pos)}
                  onTouchStart={(e) => handlePointerDown(e, pos)}
                />
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="12"
                  fill="#DDA248"
                  stroke="#fff"
                  strokeWidth="3"
                  style={{ pointerEvents: "none" }}
                />
                <text
                  x={point.x}
                  y={point.y + 4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="10"
                  fontWeight="bold"
                  style={{ pointerEvents: "none" }}
                >
                  {pos.toUpperCase()}
                </text>
              </g>
            ),
        )}

      {currentMode === MODES.MARK_CORNERS &&
        markedPoints.map(([pos, point]) => (
          <g key={pos}>
            <circle
              cx={point.x}
              cy={point.y}
              r="10"
              fill="#DDA248"
              stroke="#fff"
              strokeWidth="2"
              style={{ pointerEvents: "none" }}
            />
            <text
              x={point.x}
              y={point.y + 4}
              textAnchor="middle"
              fill="#fff"
              fontSize="9"
              fontWeight="bold"
              style={{ pointerEvents: "none" }}
            >
              {pos.toUpperCase()}
            </text>
          </g>
        ))}
    </svg>
  );
}
